import { useCallback, useState } from 'react';

import { useTimeoutFn } from 'react-use';

import { MAX_MESSAGE_LENGTH } from '@/common/constants';

const CHAR_LIMIT_ERROR_DURATION_MS = 2000;

function truncateToLimit(value: string): string {
  return value.slice(0, MAX_MESSAGE_LENGTH);
}

export function useCharacterLimit() {
  const [showCharLimitError, setShowCharLimitError] = useState(false);

  const [, , resetTimeout] = useTimeoutFn(() => {
    setShowCharLimitError(false);
  }, CHAR_LIMIT_ERROR_DURATION_MS);

  const triggerCharLimitError = useCallback(() => {
    setShowCharLimitError(true);
    resetTimeout();
  }, [resetTimeout]);

  const handleInput = useCallback(
    (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
      const target = e.target as HTMLTextAreaElement;

      if (target.value.length > MAX_MESSAGE_LENGTH) {
        const cursorPosition = target.selectionStart;
        target.value = truncateToLimit(target.value);
        const newCursorPosition = Math.min(cursorPosition, target.value.length);
        target.setSelectionRange(newCursorPosition, newCursorPosition);

        triggerCharLimitError();
      }
    },
    [triggerCharLimitError],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      e.preventDefault();

      const pastedText = e.clipboardData.getData('text');
      const target = e.target as HTMLTextAreaElement;
      const { value, selectionStart = 0, selectionEnd = 0 } = target;

      const newValue = value.slice(0, selectionStart) + pastedText + value.slice(selectionEnd);
      const truncated = truncateToLimit(newValue);
      const newCursorPosition = Math.min(selectionStart + pastedText.length, truncated.length);

      target.value = truncated;
      target.setSelectionRange(newCursorPosition, newCursorPosition);

      if (newValue.length > MAX_MESSAGE_LENGTH) {
        triggerCharLimitError();
      }

      return { value: truncated, cursorPosition: newCursorPosition };
    },
    [triggerCharLimitError],
  );

  return {
    showCharLimitError,
    triggerCharLimitError,
    handleInput,
    handlePaste,
    truncateToLimit,
  };
}
